import React from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Header, Footer, SEO } from '../../../shared/components';

const APP_NAME = import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners';
const LEGAL_NAME = 'RRD Planners and Constructions';

const AboutPage: React.FC = () => {
  return (
    <PageLayout size="full">
      <SEO title={`About Us - ${APP_NAME}`} description="Learn about our mission and services" />
      <Header />
      <PageLayout.Content>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold text-foreground">About Us</h1>
              <p className="text-muted-foreground">Professional plot map generation made simple</p>
            </div>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Who We Are</h2>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>{LEGAL_NAME}</strong> is a Warangal-based firm specializing in construction planning and survey documentation. With years of experience in the real estate and construction industry in the Telangana region, we understand the daily challenges faced by surveyors, real estate professionals, and document writers when creating cadastral plot maps.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">What We Do</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {APP_NAME} is our online platform that transforms the tedious process of creating A4 registration-ready plot maps into a quick, accurate, and affordable experience. Our platform offers:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>Regular Plot Maps</strong> - Square and rectangular plot maps with precise dimensions</li>
                  <li><strong>Irregular Plot Maps</strong> - Trapezoid and irregular shapes using surveyor-standard trilateration</li>
                  <li><strong>Room Layouts</strong> - Plot maps with ACC roof house room layouts included</li>
                  <li><strong>AutoCAD-Standard Accuracy</strong> - Shoelace formula area calculations with exact edge lengths</li>
                  <li><strong>Instant PDF Downloads</strong> - Print-ready A4 documents with professional formatting</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Our Mission</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To make professional-quality cadastral plot map generation accessible to every surveyor, document writer, and real estate professional — without requiring expensive CAD software or specialized training. We believe accurate documentation should be affordable, fast, and available to everyone.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">How It Works</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                  {[
                    { step: '1', title: 'Enter Dimensions', desc: 'Input your plot measurements in feet and inches' },
                    { step: '2', title: 'Customize Details', desc: 'Add vendor/vendee info, boundaries, and labels' },
                    { step: '3', title: 'Preview & Adjust', desc: 'Fine-tune positioning, text sizes, and layout' },
                    { step: '4', title: 'Download PDF', desc: 'Get your print-ready A4 plot map instantly' },
                  ].map(item => (
                    <div key={item.step} style={{ background: '#141414', border: '1px solid #222', borderRadius: 12, padding: 20 }}>
                      <div style={{ width: 32, height: 32, background: 'rgba(255,217,61,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFD93D', fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
                        {item.step}
                      </div>
                      <h3 style={{ color: '#e5e5e5', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{item.title}</h3>
                      <p style={{ color: '#666', fontSize: 13 }}>{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Pricing</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use a simple credit-based system. Purchase credit packs and use them to download plot maps. All prices are in Indian Rupees (INR):
                </p>
                <div style={{ background: '#141414', border: '1px solid #222', borderRadius: 12, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #222' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', color: '#888', fontWeight: 600 }}>Service</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', color: '#888', fontWeight: 600 }}>New Plan</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', color: '#888', fontWeight: 600 }}>Edit/Re-download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: 'Regular Plot', t1: 120, t2: 20 },
                        { name: 'Regular + Room', t1: 140, t2: 20 },
                        { name: 'Irregular Plot', t1: 160, t2: 20 },
                        { name: 'Irregular + Room', t1: 180, t2: 20 },
                      ].map(svc => (
                        <tr key={svc.name} style={{ borderBottom: '1px solid #1a1a1a' }}>
                          <td style={{ padding: '10px 16px', color: '#ccc' }}>{svc.name}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#FFD93D', fontWeight: 600 }}>&#8377;{svc.t1}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#888' }}>&#8377;{svc.t2}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Payment Methods</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We accept all major payment methods through our secure Razorpay payment gateway:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                  <li>UPI (Google Pay, PhonePe, Paytm, etc.)</li>
                  <li>Credit & Debit Cards (Visa, Mastercard, RuPay)</li>
                  <li>Net Banking (all major Indian banks)</li>
                  <li>Wallets (Paytm, Mobikwik, etc.)</li>
                </ul>
              </section>
            </div>

            <div className="text-center pt-8 border-t border-border">
              <a href="/" className="inline-flex items-center text-primary hover:underline font-medium">← Back to Home</a>
            </div>
          </div>
        </div>
      </PageLayout.Content>
      <Footer />
    </PageLayout>
  );
};

export default AboutPage;
